import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, CircularProgress } from "@mui/material";
import { icon, IconComponent, createNotification } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import { ApiService } from "../../../../api";
import {
	setSelectedCommitment,
	setSidePanelOpen,
	setPage,
	fetchCommitments,
	updateThemeList,
	deleteCommitments,
	validateCommitment,
	decreaseThemeCount,
	setSelectedRows,
	setSelectionMode,
} from "../../slice/commitmentTrackingSlice";
import { buildFilterPayload } from "../../utils";
import ConfirmationDialog from "../commitment-sidepanel/ConfirmationDialog";
import styles from "./CommitmentList.module.css";
import { isNonEmptyArray } from "../../../../common/utils";
import CommitmentRow from "./components/CommitmentRow";
import CommitmentRowHeader from "./components/CommitmentRowHeader";

export default function CommitmentList() {
	const dispatch = useDispatch();
	const projectId = useSelector(({ context }) => context.project?.id);
	const cancelTokenSourceRef = useRef(null);
	const listContainerRef = useRef(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const commitments = useSelector(({ commitmentTracking }) => commitmentTracking.commitments);
	const totalCommitments = useSelector(({ commitmentTracking }) => commitmentTracking.totalCommitments);
	const page = useSelector(({ commitmentTracking }) => commitmentTracking.page);
	const limit = useSelector(({ commitmentTracking }) => commitmentTracking.limit);
	const selectedTheme = useSelector(({ commitmentTracking }) => commitmentTracking.selectedTheme);
	const selectedThemeUuid = useSelector(({ commitmentTracking }) => commitmentTracking.selectedThemeUuid);
	const themeFieldUuid = useSelector(({ commitmentTracking }) => commitmentTracking.themeFieldUuid);
	const appliedFilters = useSelector(({ commitmentTracking }) => commitmentTracking.appliedFilters);
	const filterDefinitions = useSelector(({ commitmentTracking }) => commitmentTracking.filterDefinitions);
	const isLoadingCommitments = useSelector(({ commitmentTracking }) => commitmentTracking.isLoadingCommitments);
	const selectedRows = useSelector(({ commitmentTracking }) => commitmentTracking.selectedRows);
	const selectionMode = useSelector(({ commitmentTracking }) => commitmentTracking.selectionMode);
	const launchProgress = useSelector(({ commitmentTracking }) => commitmentTracking.progress);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		dispatch(setSelectedRows([]));
		dispatch(setSelectionMode("none"));
	}, [dispatch, selectedTheme]);
	useEffect(() => {
		if (launchProgress) {
			dispatch(setSelectedCommitment(null));
			dispatch(setSidePanelOpen(false));
		}
	}, [dispatch, launchProgress]);
	const buildCommitmentPayload = useCallback(
		() => buildFilterPayload(filterDefinitions, appliedFilters),
		[appliedFilters, filterDefinitions]
	);
	const loadMoreCommitments = useCallback(async () => {
		const currentCount = commitments.length;
		if (currentCount >= totalCommitments || isLoadingMore) {
			return;
		}
		setIsLoadingMore(true);
		const nextPage = page + 1;
		dispatch(setPage(nextPage));
		const cancelToken = cancelTokenSourceRef.current?.token;
		const payload = buildCommitmentPayload();
		try {
			await dispatch(fetchCommitments({ projectId, payload, page: nextPage, limit, cancelToken })).unwrap();
		} catch (error) {
			console.error("Error loading more commitments:", error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [dispatch, projectId, page, limit, totalCommitments, commitments.length, isLoadingMore, buildCommitmentPayload]);
	useEffect(() => {
		const listContainer = listContainerRef.current;
		if (!listContainer) {
			return undefined;
		}
		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = listContainer;
			if (scrollHeight - scrollTop - clientHeight < 200 && !isLoadingMore) {
				loadMoreCommitments();
			}
		};
		listContainer.addEventListener("scroll", handleScroll);
		return () => listContainer.removeEventListener("scroll", handleScroll);
	}, [loadMoreCommitments, isLoadingMore]);
	const handleSelectRow = (commitmentId) => {
		if (selectionMode === "none" || selectionMode === "include") {
			if (selectedRows.includes(commitmentId)) {
				const newRows = selectedRows.filter((id) => id !== commitmentId);
				dispatch(setSelectedRows(newRows));
				if (newRows.length === 0) {
					dispatch(setSelectionMode("none"));
				}
			} else {
				dispatch(setSelectedRows([...selectedRows, commitmentId]));
				dispatch(setSelectionMode("include"));
			}
		} else if (selectionMode === "exclude") {
			if (selectedRows.includes(commitmentId)) {
				dispatch(setSelectedRows(selectedRows.filter((id) => id !== commitmentId)));
			} else {
				dispatch(setSelectedRows([...selectedRows, commitmentId]));
			}
		}
	};
	const handleSelectAll = () => {
		if (selectionMode === "exclude" && selectedRows.length === 0) {
			dispatch(setSelectionMode("none"));
			dispatch(setSelectedRows([]));
		} else {
			dispatch(setSelectionMode("exclude"));
			dispatch(setSelectedRows([]));
		}
	};
	const buildPayloadFromFilters = useCallback(
		(filters) => buildFilterPayload(filterDefinitions, filters),
		[filterDefinitions]
	);
	const handleCommitmentSelect = (commitment) => {
		dispatch(setSelectedCommitment(commitment));
		dispatch(setSidePanelOpen(true));
	};
	const handleValidateCommitment = async (commitmentId, currentStatus, event) => {
		if (event) {
			event.stopPropagation();
		}
		try {
			const cancelToken = cancelTokenSourceRef.current?.token;
			const newStatus = !currentStatus;
			await dispatch(validateCommitment({ commitmentId, isValidated: newStatus, cancelToken })).unwrap();
			createNotification({
				type: "success",
				message: translate("commitment-tracking.notifications.validation-updated"),
			});
		} catch (error) {
			console.error("Error validating commitment:", error);
			createNotification({
				type: "error",
				message: translate("commitment-tracking.notifications.validation-error"),
			});
		}
	};
	const handleDeleteCommitment = async () => {
		if (selectionMode === "none") {
			return;
		}
		if (selectionMode === "include" && selectedRows.length === 0) {
			return;
		}
		setConfirmDeleteOpen(true);
	};
	const handleConfirmDelete = async () => {
		setIsDeleting(true);
		try {
			const cancelToken = cancelTokenSourceRef.current?.token;
			let smartDeletePayload;
			if (selectionMode === "include") {
				smartDeletePayload = {
					filter: {},
					includeIds: selectedRows,
					excludeIds: [],
				};
			} else {
				const filterPayload = buildPayloadFromFilters(appliedFilters, true);
				if (selectedThemeUuid && themeFieldUuid) {
					const themeField = filterDefinitions.find((field) => field.fieldUuid === themeFieldUuid);
					const themeFieldName = themeField ? themeField.fieldName.toLowerCase().replace(/ /g, "_") : "theme";
					filterPayload[themeFieldName] = {
						is: true,
						matchAll: true,
						elements: [selectedThemeUuid],
					};
				}
				smartDeletePayload = {
					filter: filterPayload,
					includeIds: [],
					excludeIds: selectedRows,
				};
			}
			await dispatch(deleteCommitments({ projectId, payload: smartDeletePayload, cancelToken })).unwrap();
			if (selectedTheme) {
				const deletedRowCount =
					selectionMode === "include" ? selectedRows.length : totalCommitments - selectedRows.length;
				dispatch(decreaseThemeCount({ themeName: selectedTheme, count: deletedRowCount }));
			}
			dispatch(setSelectedRows([]));
			dispatch(setSelectionMode("none"));
			setConfirmDeleteOpen(false);
			setIsDeleting(false);
			const deletedCount =
				selectionMode === "include" ? selectedRows.length : totalCommitments - selectedRows.length;
			createNotification({
				type: "success",
				message: translate("commitment-tracking.notifications.delete-success", { count: deletedCount }),
			});
			const payload = buildPayloadFromFilters(appliedFilters, true);
			if (selectedThemeUuid && themeFieldUuid) {
				const themeField = filterDefinitions.find((field) => field.fieldUuid === themeFieldUuid);
				const themeFieldName = themeField ? themeField.fieldName.toLowerCase().replace(/ /g, "_") : "theme";
				payload[themeFieldName] = {
					is: true,
					matchAll: true,
					elements: [selectedThemeUuid],
				};
			}
			dispatch(setPage(0));
			await dispatch(fetchCommitments({ projectId, payload, page: 0, limit, cancelToken })).unwrap();
			await dispatch(
				updateThemeList({ projectId, filters: appliedFilters, themeFieldUuid, cancelToken })
			).unwrap();
		} catch (error) {
			console.error("Error deleting commitment:", error);
			setIsDeleting(false);
			setConfirmDeleteOpen(false);
			createNotification({
				type: "error",
				message: translate("commitment-tracking.notifications.delete-error"),
			});
		}
	};
	const handleCancelDelete = () => {
		setConfirmDeleteOpen(false);
	};
	const themes =
		useSelector(
			({ commitmentTracking }) =>
				commitmentTracking.filterDefinitions.find((filterDefinition) => filterDefinition.fieldName === "theme")
					?.possibleValues
		) || [];
	if (!selectedTheme) {
		const validThemes = themes.filter((theme) => {
			const commitmentCount = typeof theme === "object" ? theme.commitmentCount : 0;
			return commitmentCount > 0;
		});
		const hasThemes = validThemes.length > 0;
		return (
			<div className={styles.container}>
				{!launchProgress && (
					<div className={styles.emptyStateFullPage}>
						{hasThemes
							? translate("commitment-tracking.empty-state.select-theme")
							: translate("commitment-tracking.empty-state.no-results-with-filter")}
					</div>
				)}
			</div>
		);
	}
	const translateSelectionText = () => {
		if (selectionMode === "include") {
			return translate(
				`commitment-tracking.table.selection.list.${selectedRows.length > 1 ? "plural" : "singular"}`,
				{
					count: selectedRows.length,
					total: totalCommitments,
				}
			);
		}
		return translate(
			`commitment-tracking.table.selection.list.${
				totalCommitments - selectedRows.length > 1 ? "plural" : "singular"
			}`,
			{
				count: totalCommitments - selectedRows.length,
				total: totalCommitments,
			}
		);
	};
	return (
		<div className={styles.container}>
			<CommitmentRowHeader
				disabledDelete={selectionMode === "none" || (selectionMode === "include" && selectedRows.length === 0)}
				launchProgress={launchProgress}
				selectedTheme={selectedTheme}
				onDeleteCommitment={handleDeleteCommitment}
			/>
			<div ref={listContainerRef} className={styles.listContainer}>
				{isLoadingCommitments && (
					<div className={styles.loadingOverlay}>
						<CircularProgress size={40} />
					</div>
				)}
				{!launchProgress && (
					<div className={styles.selectionCounterRow}>
						{(isNonEmptyArray(selectedRows) ||
							(selectionMode === "exclude" && selectedRows.length === 0)) && (
							<>
								<Checkbox
									checked={selectionMode === "exclude" && selectedRows.length === 0}
									className={styles.checkbox}
									indeterminate={
										(selectionMode === "include" && isNonEmptyArray(selectedRows)) ||
										(selectionMode === "exclude" && isNonEmptyArray(selectedRows))
									}
									onClick={handleSelectAll}
								/>
								<span className={styles.selectionText}>{translateSelectionText()}</span>
							</>
						)}
					</div>
				)}
				{isNonEmptyArray(commitments) && (
					<div className={styles.commitmentContainer}>
						{commitments.map((commitment) => (
							<CommitmentRow
								key={commitment?.commitmentId}
								commitment={commitment}
								selectedRows={selectedRows}
								selectionMode={selectionMode}
								onCommitmentSelect={handleCommitmentSelect}
								onSelectRow={handleSelectRow}
								onValidateCommitment={handleValidateCommitment}
							/>
						))}
					</div>
				)}
				{isLoadingMore && commitments.length > 0 && (
					<div className={styles.loadingMoreContainer}>
						<CircularProgress size={30} />
					</div>
				)}
				{!isLoadingCommitments &&
					(!commitments || commitments.length === 0) &&
					(() => {
						const hasActiveFilters = Object.entries(appliedFilters).some(
							([key, value]) =>
								key !== "separator" &&
								key !== themeFieldUuid &&
								value &&
								(typeof value === "string"
									? value.trim() !== ""
									: (value.elements && value.elements.length > 0) || value.startDate || value.endDate)
						);
						return (
							<div className={styles.emptyState}>
								<IconComponent icon={icon.faInbox} />
								<p>
									{hasActiveFilters
										? translate("commitment-tracking.empty-state.no-results-with-filter")
										: translate("commitment-tracking.empty-state.theme-no-commitments")}
								</p>
							</div>
						);
					})()}
			</div>
			<ConfirmationDialog
				cancelLabel={translate("commitment-tracking.confirm-delete.cancel")}
				confirmLabel={
					isDeleting
						? translate("commitment-tracking.confirm-delete.deleting")
						: translate("commitment-tracking.confirm-delete.confirm")
				}
				iconColor="var(--red-500)"
				iconType={icon.faExclamationTriangle}
				message={
					selectionMode === "exclude" || selectedRows.length > 1
						? translate("commitment-tracking.dialog.delete.message-plural")
						: translate("commitment-tracking.dialog.delete.message")
				}
				open={confirmDeleteOpen}
				onClose={isDeleting ? () => null : handleCancelDelete}
				onConfirm={handleConfirmDelete}
			/>
		</div>
	);
}
